import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Voice } from '../../../../api/client';
import { voiceKey } from './model';

export type PreviewState = 'idle' | 'loading' | 'playing';

/** One shared player for voice previews; blob URLs are cached per voice for the visit. */
export function usePreview(fetchUrl: (v: Voice) => Promise<string>) {
  const { t } = useTranslation('assistantCompact');
  const audio = useRef<HTMLAudioElement | null>(null);
  const urls = useRef(new Map<string, string>());
  const [playing, setPlaying] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const map = urls.current;
    return () => {
      audio.current?.pause();
      map.forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  const stop = () => { audio.current?.pause(); setPlaying(null); };

  const toggle = async (v: Voice) => {
    const key = voiceKey(v);
    if (playing === key) return stop();
    stop();
    setError(null);
    let url = urls.current.get(key);
    if (!url) {
      setLoading(key);
      try {
        url = await fetchUrl(v);
        urls.current.set(key, url);
      } catch (e) {
        setError(e instanceof Error ? e.message : t('au.v.previewFailed'));
        return;
      } finally {
        setLoading(null);
      }
    }
    const el = audio.current ?? (audio.current = new Audio());
    el.src = url;
    el.onended = () => setPlaying(null);
    setPlaying(key);
    el.play().catch(() => setPlaying(null));
  };

  const stateOf = (v: Voice): PreviewState => (loading === voiceKey(v) ? 'loading' : playing === voiceKey(v) ? 'playing' : 'idle');
  return { toggle, stateOf, error };
}
